import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { ShieldCheck, ChevronLeft } from "lucide-react";
import { API, LOGO_URL } from "@/config";
import { Fireflies } from "@/components/effects/Fireflies";

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const ExtraChargeForm = ({ chargeId, amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    const returnUrl = `${window.location.origin}/booking-success?extra_charge=${chargeId}`;
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    });
    if (error) { toast.error(error.message || "Payment failed."); setSubmitting(false); }
  };
  return (
    <form onSubmit={submit} className="glass p-6 sm:p-8" data-testid="extra-charge-form">
      <PaymentElement options={{ layout: "tabs" }} />
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="btn-primary w-full mt-5 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="extra-charge-pay-btn"
      >
        {submitting ? "Processing…" : `Pay $${amount.toFixed(2)}`}
      </button>
      <p className="text-xs text-[#B9C8D5] mt-3 text-center flex items-center justify-center gap-1">
        <ShieldCheck className="w-3 h-3" /> Secured by Stripe
      </p>
    </form>
  );
};

export const ExtraChargePage = () => {
  const [sp] = useSearchParams();
  const chargeId = sp.get("charge_id");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!chargeId) { setError("no-id"); return; }
    axios.get(`${API}/checkout/extra-charge/${chargeId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "not-found"));
  }, [chargeId]);

  if (!stripePromise) return <div className="min-h-screen flex items-center justify-center text-red-300">Stripe not configured</div>;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass p-8 max-w-md text-center" data-testid="extra-charge-error">
          <h1 className="font-serif text-3xl text-[#FFD166] mb-3">Charge not available</h1>
          <p className="text-sm text-[#B9C8D5] mb-6">{typeof error === "string" ? error : "This link may have expired or been paid already."}</p>
          <Link to="/" className="btn-ghost">Go home</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass p-10 text-center" data-testid="extra-charge-loading">
          <p className="font-serif text-xl">Loading…</p>
        </div>
      </div>
    );
  }

  const appearance = {
    theme: "night",
    variables: {
      colorPrimary: "#FFD166", colorBackground: "#0F1E2C", colorText: "#F6F1E4",
      fontFamily: "system-ui, sans-serif", borderRadius: "12px",
    },
  };

  return (
    <div className="min-h-screen bg-[#04090F] no-custom-cursor relative overflow-hidden">
      <Fireflies count={12} />
      <div className="relative z-10">
        <header className="border-b border-[#FFD166]/15 bg-[#0A1A26]/60 backdrop-blur">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-[#FFD166] hover:text-[#F4A93A] text-sm">
              <ChevronLeft className="w-4 h-4" /> Enchanted Gear
            </Link>
            <img src={LOGO_URL} alt="Enchanted Gear Rentals" className="h-10" style={{ filter: "url(#knockoutBlack)" }} />
            <div className="w-24 text-right text-[10px] uppercase tracking-widest text-[#B9C8D5]">Additional charge</div>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
          <h1 className="font-serif text-4xl text-[#FFD166] mb-2">Additional charge</h1>
          <p className="text-sm text-[#B9C8D5] mb-8">Hi {data.reservation?.customer_name || "friend"} — please review + pay the balance below.</p>

          <div className="glass p-6 sm:p-8 mb-6" data-testid="extra-charge-summary">
            <div className="flex justify-between items-start mb-3">
              <div className="text-[#F6F1E4]">{data.description}</div>
              <div className="text-2xl font-serif text-[#FFD166]">${data.amount.toFixed(2)}</div>
            </div>
            {data.reservation?.short_code && (
              <div className="text-[10px] text-[#B9C8D5] font-mono">Booking {data.reservation.short_code}</div>
            )}
          </div>

          <Elements stripe={stripePromise} options={{ clientSecret: data.client_secret, appearance }}>
            <ExtraChargeForm chargeId={chargeId} amount={data.amount} />
          </Elements>
        </main>
      </div>
    </div>
  );
};

export default ExtraChargePage;
